/**
 * CR412: Date-only helpers to prevent timezone day-shift.
 * 
 * Problem: Dates stored as timestamptz at UTC midnight (e.g., 2026-02-05T00:00:00+00)
 * shift back one day when rendered in America/Toronto timezone.
 * 
 * Solution: Extract the date-only string ("YYYY-MM-DD") and parse it as a local
 * calendar date (no timezone conversion).
 * 
 * RULE: Never do `new Date('YYYY-MM-DD')` for deal validity dates — it creates
 * a UTC midnight date that shifts when displayed in local timezone.
 */

/**
 * Extract a date-only string ("YYYY-MM-DD") from any date input.
 * 
 * - If already "YYYY-MM-DD", returns as-is.
 * - If a timestamp string, extracts the UTC date portion.
 * - Returns null for null/undefined/invalid inputs.
 */
export function toDateOnlyString(input: any): string | null {
  if (!input) return null;

  if (typeof input === "string") {
    // Already date-only
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return input;

    // Extract date portion from ISO-like timestamps or "YYYY-MM-DD ..." strings
    const head = input.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(head) && input.length > 10) return head;

    // Fallback: try Date parsing for other formats
    const d = new Date(input);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  }

  // Date object
  if (input instanceof Date && !isNaN(input.getTime())) {
    const yyyy = input.getFullYear();
    const mm = String(input.getMonth() + 1).padStart(2, "0");
    const dd = String(input.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  return null;
}

/**
 * Parse a "YYYY-MM-DD" string into a local Date object.
 * 
 * This creates a Date at midnight in the LOCAL timezone, preventing
 * the day-shift that occurs when `new Date('YYYY-MM-DD')` interprets
 * the string as UTC midnight.
 * 
 * @example
 * // In America/Toronto (UTC-5):
 * new Date('2026-02-05')          → Feb 4, 2026 19:00 EST (WRONG)
 * parseDateOnlyToLocal('2026-02-05') → Feb 5, 2026 00:00 EST (CORRECT)
 */
export function parseDateOnlyToLocal(yyyyMmDd: string): Date {
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  return new Date(y, m - 1, d); // local calendar date, no TZ shift
}
